import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import { ThrowError } from "@/api/callApi";
import { ApplicationService } from "@/services/Applications/ApplicationService";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import type { Application } from "@/interface/applications.interface";
import { Button } from "@/components/ui/button";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import TablePagination from "@/components/common/TablePagination";
import { Plus, TriangleAlert } from "lucide-react";
import TableActions from "@/components/common/TableActions";
import CommonModal from "@/components/common/CommonModal";
import CommonFilter from "@/components/common/CommonFilter";

const ApplicationPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const [filter, setFilter] = useState<IFilter>({
    search: "",
  });

  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const getApplications = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      const apiFilter = {
        ...filter,
      };
      const paramsURL = GetFilterUrl(apiFilter);
      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""}`;
      const response = await ApplicationService.getAllApplications(PATH);
      if (response?.status) {
        setApplications(response?.data?.clients || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getApplications(paginationData.currentPage, paginationData.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const toggleDeleteDialog = (id: string = "") => {
    setDeleteId(id);
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleDelete = async () => {
    try {
      const response = await ApplicationService.deleteApplication(deleteId);
      if (response?.status) {
        showToastNotification("success", response.message);
        toggleDeleteDialog();
        getApplications();
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  const columns: TableColumn<Application>[] = [
    {
      key: "srNo",
      header: "Sr No",
      render: (_, index) => (
        <span className="text-muted-foreground font-medium"># {index + 1}</span>
      ),
    },
    {
      key: "name",
      header: "Application Name",
      render: (item) => (
        <span className="font-semibold capitalize">{item.name}</span>
      ),
    },
    {
      key: "clientId",
      header: "Client ID",
      render: (item) => <span className="text-gray-600">{item.clientId}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${item.isActive ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}
        >
          {item.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <TableActions
          onEdit={() => navigate(`${RoutesLink.editApplication}/${item.id}`)}
          editLabel="Edit App"
          onDelete={() => toggleDeleteDialog(item.id)}
          deleteLabel="Delete App"
        />
      ),
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title="Applications"
          buttonText="Add Application"
          buttonIcon={<Plus />}
          isBackButtonVisible={true}
          onClick={() => navigate(RoutesLink.addApplication)}
        />
        <CommonFilter
          searchValue={filter.search || ""}
          onSearchChange={handleSearchChange}
        />
        <CommonTable
          data={applications}
          columns={columns}
          loading={isLoading}
        />
        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />
      </div>

      <CommonModal
        show={deleteDialogOpen}
        onClose={() => toggleDeleteDialog()}
        title=""
        className="max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <TriangleAlert className="text-red-600 w-8 h-8" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              Delete Application?
            </h2>
            <p className="mt-3 text-sm leading-6 text-gray-500">
              This action cannot be undone. Once deleted, the application access
              will be permanently revoked.
            </p>
          </div>
          <div className="mt-8 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleDeleteDialog()}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </CommonModal>
    </Layout>
  );
};

export default ApplicationPage;
